import { createApi } from '@reduxjs/toolkit/query/react';
import { isNil } from 'lodash';

import { CompanyResponse } from '../types/company';
import { createBaseQuery } from './api/baseApiConfig';
import config from '../config';
import { CompanyNote } from 'types/CompanyNote';

type FetchCompany = {
	companyId: string | null;
};

export interface CompanySearchParams {
	name: string;
}

export interface CompanyData {
	businessId: string;
	companyId: string;
	name: string;
	registrationDate: string;
	companyForm: string;
	isCustomer?: boolean;
}

export interface CompanySearchResponse {
	data: CompanyData;
}

const Tags = {
	Company: 'company',
	CompanyNotes: 'companyNotes',
};

export const companyApi = createApi({
	reducerPath: 'companyApi',
	baseQuery: createBaseQuery(`${config.APIM_BASE_URL}/${config.COMPANY_API_URL}`),
	tagTypes: [Tags.Company, Tags.CompanyNotes],
	endpoints: builder => ({
		searchCompany: builder.query<CompanySearchResponse, CompanySearchParams>({
			query: ({ name }) => {
				return {
					url: `/company/name/${name}`,
					method: 'GET',
				};
			},
		}),
		fetchCompany: builder.query<CompanyResponse, FetchCompany>({
			query: ({ companyId }) => {
				if (isNil(companyId)) {
					throw new Error('parameter error');
				}
				return {
					method: 'GET',
					url: `/v2/company/${companyId}`,
				};
			},
			providesTags: (_data, _error, params) => {
				return [{ type: Tags.Company, id: params?.companyId ?? '' }];
			},
		}),
		fetchCompanyNotes: builder.query<CompanyNote[], { companyId: string }>({
			query: ({ companyId }) => {
				if (isNil(companyId)) {
					throw new Error('parameter error');
				}
				return {
					method: 'GET',
					url: `/v2/company/${companyId}/notes`,
				};
			},
			providesTags: (_data, _error, params) => {
				return [{ type: Tags.CompanyNotes, id: params?.companyId ?? '' }];
			},
		}),
	}),
});

export const { useSearchCompanyQuery, useFetchCompanyQuery, useFetchCompanyNotesQuery } = companyApi;
